import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import Footer from '../components/Footer';
import axios from 'axios';
import { ArrowLeft, Calendar, Sparkles, Plus, Pencil, Trash2, ExternalLink } from 'lucide-react';
import ChangelogEditorDrawer from '../components/ChangelogEditorDrawer';
import { useWorkspace } from '../contexts/WorkspaceContext';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { API_URL } from '../lib/apiBase';

const ChangelogPage = () => {
  const navigate = useNavigate();
  const { currentWorkspace, currentWorkspaceId } = useWorkspace();
  const [changelog, setChangelog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [deletingEntry, setDeletingEntry] = useState(null);

  useEffect(() => {
    if (currentWorkspaceId) fetchChangelog();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentWorkspaceId]);

  const fetchChangelog = async () => {
    try {
      setLoading(true);
      const params = currentWorkspaceId ? `?workspace_id=${currentWorkspaceId}` : '';
      const response = await axios.get(`${API_URL}/api/changelog${params}`);
      setChangelog(response.data.entries || []);
    } catch (error) {
      console.error('Error fetching changelog:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingEntry) return;
    try {
      await axios.delete(`${API_URL}/api/changelog/${deletingEntry.id}`, {
        params: { workspace_id: currentWorkspaceId },
      });
      toast.success('Entry deleted');
      setDeletingEntry(null);
      fetchChangelog();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="border-b bg-card">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                onClick={() => navigate('/app')}
                data-testid="changelog-back-button"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Dashboard
              </Button>
              <div>
                <h1 className="text-2xl font-bold">Changelog</h1>
                <p className="text-sm text-muted-foreground">See what's been shipped recently</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {currentWorkspace?.public_changelog && currentWorkspace?.slug && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => window.open(`/c/${currentWorkspace.slug}`, '_blank')}
                  data-testid="changelog-view-public"
                >
                  <ExternalLink className="h-4 w-4 mr-1.5" />
                  View public
                </Button>
              )}
              <Button
                className="btn-cta"
                size="sm"
                onClick={() => { setEditingEntry(null); setEditorOpen(true); }}
                data-testid="changelog-new-entry"
              >
                <Plus className="h-4 w-4 mr-1.5" />
                New entry
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Changelog Entries */}
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-3xl mx-auto space-y-6">
          {changelog.length === 0 ? (
            <Card className="p-12 text-center">
              <Sparkles className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
              <h3 className="text-lg font-semibold mb-2">No shipped features yet</h3>
              <p className="text-sm text-muted-foreground">
                Mark features as "Shipped" from the dashboard to see them here.
              </p>
            </Card>
          ) : (
            changelog.map((entry, index) => (
              <motion.div
                key={entry.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="p-6 space-y-4">
                  {/* Entry Header */}
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <Sparkles className="h-5 w-5 text-green-500" />
                        <h2 className="text-lg font-semibold">{entry.title}</h2>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Calendar className="h-4 w-4" />
                        <span>{formatDate(entry.shipped_at)}</span>
                      </div>
                    </div>
                    <Badge className="bg-green-500 text-white">Shipped</Badge>
                    <div className="flex items-center gap-1 ml-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0"
                        onClick={() => { setEditingEntry(entry); setEditorOpen(true); }}
                        aria-label="Edit entry"
                        data-testid={`changelog-edit-${entry.id}`}
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0 text-destructive hover:text-destructive"
                        onClick={() => setDeletingEntry(entry)}
                        aria-label="Delete entry"
                        data-testid={`changelog-delete-${entry.id}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>

                  <Separator />

                  {/* Entry Description */}
                  <div>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {entry.description}
                    </p>
                  </div>

                  {/* Category */}
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary" className="text-xs">
                      {entry.category.replace('_', ' ')}
                    </Badge>
                    {entry.signal_ids && entry.signal_ids.length > 0 && (
                      <span className="text-xs text-muted-foreground">
                        Based on {entry.signal_ids.length} user request{entry.signal_ids.length > 1 ? 's' : ''}
                      </span>
                    )}
                  </div>
                </Card>
              </motion.div>
            ))
          )}
        </div>
      </div>

      {/* Footer */}
      <Footer variant="minimal" />

      {/* Editor drawer */}
      <ChangelogEditorDrawer
        open={editorOpen}
        onOpenChange={setEditorOpen}
        entry={editingEntry}
        workspaceId={currentWorkspaceId}
        onSaved={fetchChangelog}
      />

      {/* Delete confirmation */}
      <AlertDialog open={!!deletingEntry} onOpenChange={(o) => !o && setDeletingEntry(null)}>
        <AlertDialogContent data-testid="changelog-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this changelog entry?</AlertDialogTitle>
            <AlertDialogDescription>
              "{deletingEntry?.title}" will be permanently removed. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="changelog-delete-confirm-button"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ChangelogPage;
